




























































import { Component, Vue, Prop } from 'vue-property-decorator'
import { Card } from '@alpha/ink'
import NotificationSettings from '@/components/WorkspaceSettings/NotificationSettings.vue'
import IconSettings from '@/components/WorkspaceSettings/IconSettings.vue'
import SlackSettings from '@/components/WorkspaceSettings/SlackSettings.vue'
import ColorSettings from '@/components/WorkspaceSettings/ColorSettings.vue'
import InviteUser from '@/components/WorkspaceSettings/InviteUser.vue'
import ManageUsers from '@/components/WorkspaceSettings/ManageUsers.vue'
import PageHeader from '@/components/PageHeader.vue'
import Toast from '@/components/Toast.vue'
import { Crumb } from '@/components/Breadcrumbs.vue'

import store from '@/store'
import api from '@/api'
import { WorkspacePreferencesQuery } from '@/api/graphql-sdk.generated'
import * as db from '@shared/types/database'
import { UserPreferences } from '@shared/types/database'
import analytics from '@/analytics'
import { VUE_APP_SLACK_CLIENT_ID } from '@/config'
import { ColorBranding } from '@/store/modules/workspace-store-module'
import { WorkspaceSettingsMessages } from '@/store/modules/notification-store-module'

export interface SettingsConfirmation {
  status: boolean|string,
  msg: string
}

export interface SettingsSubmissionData {
  component: {
    finish(res: SettingsSubmissionResponse): void
  },
  section: string,
  data: {
    [key: string]: any
  }
}

export interface SettingsSubmissionResponse {
  status: 'success'|'failure',
  msg: string
}

export interface SlackIntegrationBlob {
  state: string,
  id: string
}

interface SlackConfig {
  team: string,
  channel: string,
  // eslint-disable-next-line
  configuration_url: string
}

export type SlackClientConfig = SlackConfig | false

export type WorkspaceUsers = WorkspacePreferencesQuery['workspaceUsers']
export type WorkspaceUser = WorkspaceUsers[0]

export interface RequestUserResponse {
  status: 'error'|'success'|'invite',
  msg: string,
  user: { id: string },
  type?: 'new'
}

export interface InviteUserResponse {
  msg: string,
  user?: db.User & { payload: { email: string }},
  type: 'new'|'duplicate'|'existing',
  uid: string,
  wsid: string
}

export interface InviteFormFields {
  newUsersEmail: string,
  newUsersFirstName: string,
  newUsersLastName: string,
  [key: string]: string
}

@Component({
  name: 'workspaceSettings',
  components: {
    Card,
    IconSettings,
    NotificationSettings,
    SlackSettings,
    InviteUser,
    ManageUsers,
    PageHeader,
    Toast,
    ColorSettings
  }
})
export default class WorkspaceSettings extends Vue {
  @Prop({ default: () => '' }) assetloc!: string
  @Prop({ default: () => [] }) orgUsers!: []

  loading: boolean = true
  colorsAreSaving: boolean = false
  workspacePreferences: UserPreferences = {}
  workspaceUsers: WorkspaceUser[] = []
  slackClientInfo: SlackClientConfig = false
  error: string|null = null

  get colorBranding () {
    return store.state.workspace.branding
  }

  async submitBranding (branding: ColorBranding) {
    this.colorsAreSaving = true
    await store.dispatch.workspace.updateBranding(branding)
    this.toast = WorkspaceSettingsMessages.updateColorSuccess
    this.colorsAreSaving = false
  }

  get crumbs (): Crumb[] {
    const { workspace } = store.state.workspace
    return [
      {
        title: workspace.name,
        url: `/workspaces/${workspace.id}/`
      },
      { title: 'Settings' }
    ]
  }

  get workspace () {
    return store.state.workspace.workspaceWithTests
  }

  get isAdmin (): boolean {
    if (this.isAlphaAdmin) return true
    const access = store.state.user.authenticatedUser!.workspaceAccessLevels.find(access => access.id === this.workspace.id)
    if (!access || access.accessLevel !== 'workspace_admin') {
      return false
    }
    return true
  }

  get isAlphaAdmin () {
    return store.state.user.authenticatedUser!.admin
  }

  get workspaceLogo () {
    return store.state.workspace.workspace.payload.icon
  }

  get thisWorkspacePreferences () {
    if (!this.workspacePreferences[store.state.workspace.workspace.id]) return {}
    return this.workspacePreferences[store.state.workspace.workspace.id]
  }

  get clientName () {
    return store.state.workspace.workspace.name
  }

  get iconUrl () {
    return store.state.workspace.workspace.payload.icon
  }

  get slackIntegration (): SlackIntegrationBlob {
    return {
      state: this.workspace.id,
      id: VUE_APP_SLACK_CLIENT_ID!
    }
  }

  set toast (value: string | false) {
    store.dispatch.notification.setMessage(value)
  }

  get toast () {
    return store.state.notification.message
  }

  async mounted () {
    const res = await api.getWorkspacePreferences(this.workspace.id)
    this.workspacePreferences = res.userPayload?.preferences ?? {}
    this.workspaceUsers = res.workspaceUsers
    this.loading = false
    this.slackClientInfo = (store.state.workspace.workspace.payload && store.state.workspace.workspace.payload.slack) || false
  }

  formatNewUser (event: InviteUserResponse): WorkspaceUser {
    return {
      payload: event!.user!.payload,
      id: event!.uid,
      email: event!.user!.payload.email!
    }
  }

  addUser (event: InviteUserResponse) {
    switch (event.type) {
      case 'new':
        this.workspaceUsers.push(this.formatNewUser(event))
        this.toast = WorkspaceSettingsMessages.inviteUserSuccess
        break
      case 'existing':
        this.workspaceUsers.push(this.formatNewUser(event))
        this.toast = WorkspaceSettingsMessages.addUserSuccess
        break
      case 'duplicate':
        this.toast = event.msg
        break
    }
  }

  removeUser (userId: string) {
    const userIndex = this.workspaceUsers.findIndex(u => u.id === userId)
    if (userIndex !== -1) {
      this.workspaceUsers.splice(userIndex, 1)
    }
    this.toast = WorkspaceSettingsMessages.removeUserSuccess
  }

  submit (event: SettingsSubmissionData) {
    api.updateWorkspaceSetting(this.workspace.id, event.section, event.data)
      .then((res) => {
        const dataObj: SettingsSubmissionResponse = res.data
        event.component.finish(dataObj)
        analytics.track('Settings - ' + event.section)
        if (event.section === 'revoke-slack' && dataObj.status !== 'failure') {
          this.slackClientInfo = false
        }
      })
      .catch((error) => {
        console.error(error)
        const errorResponse: SettingsSubmissionResponse = {
          status: 'failure',
          msg: 'An error occured! Please try again.'
        }
        event.component.finish(errorResponse)
      })
  }
}
